import type { Drawing, StrokeBrush } from '../shared/art/types';
import { PALETTES } from '../shared/account';

export type LessonLevel = 0 | 1 | 2;
export type OriginalSubject = 'cat' | 'panda' | 'sunflower' | 'strawberry' | 'icecream' | 'house' | 'boat' | 'balloon' | 'rocket' | 'fish';
export type Subject = OriginalSubject | 'rabbit' | 'penguin' | 'turtle' | 'butterfly' | 'snail' | 'mushroom' | 'watermelon' | 'umbrella' | 'whale' | 'lighthouse'
  | 'fox' | 'swan' | 'dolphin' | 'jellyfish' | 'owl'
  | 'lotus' | 'rose' | 'cherry' | 'cactus' | 'grapes'
  | 'castle' | 'windmill' | 'piano' | 'bicycle' | 'coffee';
export type XY = [number, number];
export interface DrawingLesson {
  id: string; title: string; skill: string; level: LessonLevel; style: string;
  keywords: string[];
  drawing: Drawing;
  steps: { title: string; tip: string; at: number }[];
  palettes: string[]; brushes: StrokeBrush[];
}
export interface DrawingExample {
  id: string;
  title: string;
  skill: string;
  keywords: string[];
  /** 明确登记对应题目；场景或技法关键词不能用作覆盖依据。 */
  wordIds: string[];
  artDirection?: { composition: string; light: string; palette: string; technique: string };
  create: (level: LessonLevel) => DrawingLesson;
}
export const LESSON_LEVELS = [
  { label: '入门', description: '六色手绘 · 轮廓与排线' },
  { label: '进阶', description: '柔和配色 · 明暗与材质' },
  { label: '艺术', description: '多笔头创作 · 场景与光影' },
] as const;
export const K = '#000000', R = '#E53935', Y = '#F5C518', G = '#2FA77A', B = '#1E63D0', W = '#FFFFFF';
export type LessonTheme = { colors: string[]; shade: string; light: string; paper: string; accent: string; style: string };
type Theme = LessonTheme;
// 所有用色来自现有色包，教程列出实际使用的色包，不额外解锁玩家资产。
const ORIGINAL_THEMES: Record<OriginalSubject, Theme> = {
  cat: { colors: ['#6F4E37', '#EA8258', '#EEC759', '#73A797', '#7799C4', '#FFF1E6'], shade: '#A9805B', light: '#FFD3B6', paper: '#F5EBDD', accent: '#8FA85C', style: '暖窗花房' },
  panda: { colors: ['#12261E', '#E68AA8', '#D6E8B0', '#5B8C4A', '#7FA8C9', '#F2F7E6'], shade: '#7FB08A', light: '#C7D9B8', paper: '#F0F3E4', accent: '#2F5D3A', style: '竹影清风' },
  sunflower: { colors: ['#7A5C3E', '#C0693C', '#FFB627', '#4F7A54', '#2F5A5E', '#FFF6E0'], shade: '#D9A05B', light: '#F4E04D', paper: '#F2E3C9', accent: '#8FA85C', style: '午后植物手记' },
  strawberry: { colors: ['#7A0F2E', '#E24B6B', '#FFE0A3', '#3F7D4E', '#5FC8C0', '#FFF1E6'], shade: '#B3123F', light: '#FFC2CB', paper: '#FFF0F5', accent: '#7AB55C', style: '果园的下午茶' },
  icecream: { colors: ['#8A5A6A', '#F27E93', '#E8C07D', '#73A797', '#9A86D1', '#FFF1E6'], shade: '#C96A8A', light: '#FFD3B6', paper: '#FFF0F5', accent: '#C7B8EA', style: '奶油色的梦' },
  house: { colors: ['#293D38', '#C0693C', '#E8D3A9', '#4F7A54', '#7FA8C9', '#F5EBDD'], shade: '#8A6E4B', light: '#FFD98A', paper: '#EAF2F8', accent: '#9CC26B', style: '山林里的小屋' },
  boat: { colors: ['#032B44', '#C0693C', '#F4D35E', '#2E9CA8', '#0E5A78', '#F5EBDD'], shade: '#2E7EA8', light: '#FFD98A', paper: '#E8F1F2', accent: '#FFB07C', style: '落日归帆' },
  balloon: { colors: ['#4A3F6B', '#E08A7B', '#F2C79B', '#73A797', '#9A86D1', '#F5F0FF'], shade: '#A2638A', light: '#FFD3B6', paper: '#EFE6FF', accent: '#C7B8EA', style: '暮色里的远行' },
  rocket: { colors: ['#2B3E56', '#E24B6B', '#FFD166', '#5CE1E6', '#5B4FCF', '#EAF2F8'], shade: '#7FA8C9', light: '#F5F0FF', paper: '#0B1026', accent: '#9C7BFF', style: '驶向星河' },
  fish: { colors: ['#1F6F78', '#FF7F7F', '#FFB07C', '#2F6F55', '#5FC8C0', '#F5F1E8'], shade: '#E08A7B', light: '#FFE0A3', paper: '#E8F1F2', accent: '#7FD6D0', style: '池塘浮光' },
};
export const THEMES: Record<Subject, Theme> = {
  ...ORIGINAL_THEMES,
  rabbit: { ...ORIGINAL_THEMES.cat, style: '月下的长耳朋友' },
  penguin: { ...ORIGINAL_THEMES.boat, style: '极地的蓝色时光' },
  turtle: { ...ORIGINAL_THEMES.panda, style: '荷池慢游' },
  butterfly: { ...ORIGINAL_THEMES.balloon, style: '花间的翅影' },
  snail: { ...ORIGINAL_THEMES.cat, style: '露水里的慢旅行' },
  mushroom: { ...ORIGINAL_THEMES.strawberry, style: '林间小伞' },
  watermelon: { ...ORIGINAL_THEMES.strawberry, style: '盛夏的红与绿' },
  umbrella: { ...ORIGINAL_THEMES.boat, style: '雨中的一束暖色' },
  whale: { ...ORIGINAL_THEMES.boat, style: '深蓝里的歌声' },
  lighthouse: { ...ORIGINAL_THEMES.boat, style: '海岬上的守望' },
  fox: { ...ORIGINAL_THEMES.cat, style: '狐影暖林' },
  swan: { ...ORIGINAL_THEMES.boat, style: '月湖羽光' },
  dolphin: { ...ORIGINAL_THEMES.boat, style: '逐浪晨光' },
  jellyfish: { ...ORIGINAL_THEMES.rocket, style: '深海灯舞' },
  owl: { ...ORIGINAL_THEMES.sunflower, style: '暮枝守夜' },
  lotus: { ...ORIGINAL_THEMES.fish, style: '荷塘初醒' },
  rose: { ...ORIGINAL_THEMES.strawberry, style: '暗香与绯光' },
  cherry: { ...ORIGINAL_THEMES.balloon, style: '樱色春信' },
  cactus: { ...ORIGINAL_THEMES.panda, style: '沙海中的花期' },
  grapes: { ...ORIGINAL_THEMES.balloon, style: '藤间凝紫' },
  castle: { ...ORIGINAL_THEMES.rocket, style: '云水间的城堡' },
  windmill: { ...ORIGINAL_THEMES.sunflower, style: '麦田里的风' },
  piano: { ...ORIGINAL_THEMES.balloon, style: '光中的夜曲' },
  bicycle: { ...ORIGINAL_THEMES.fish, style: '花篮里的晴天' },
  coffee: { ...ORIGINAL_THEMES.cat, style: '窗边的一杯时光' },
};
export const ellipse = (x: number, y: number, rx: number, ry: number, start = 0, end = Math.PI * 2): XY[] =>
  Array.from({ length: 65 }, (_, i) => [x + rx * Math.cos(start + (end - start) * i / 64), y + ry * Math.sin(start + (end - start) * i / 64)]);
export function curve(start: XY, ...segments: [XY, XY, XY][]): XY[] {
  const points = [start]; let from = start;
  for (const [a, b, to] of segments) {
    for (let i = 1; i <= 24; i++) { const t = i / 24, u = 1 - t; points.push([u ** 3 * from[0] + 3 * u * u * t * a[0] + 3 * u * t * t * b[0] + t ** 3 * to[0], u ** 3 * from[1] + 3 * u * u * t * a[1] + 3 * u * t * t * b[1] + t ** 3 * to[1]]); }
    from = to;
  }
  return points;
}
const wave = (n: number) => Math.sin(n * 1.37) * 0.6 + Math.sin(n * 0.53 + 2) * 0.4;
const close = (points: XY[]) => Math.hypot(points[0][0] - points.at(-1)![0], points[0][1] - points.at(-1)![1]) < 1 ? points : [...points, points[0]];

/** 原生笔迹写作器：不改公共画笔协议；弧度、落笔停顿与粗细变化写入真实采样。 */
export class Lesson {
  drawing: Drawing = { schemaVersion: 1, strokes: [] };
  steps: DrawingLesson['steps'] = [];
  time = 450;
  theme: Theme;
  constructor(readonly subject: string, readonly level: LessonLevel, theme?: LessonTheme) {
    const selected = theme ?? THEMES[subject as Subject];
    if (!selected) throw new Error(`例画题材 ${subject} 缺少配色设计`);
    this.theme = selected;
  }
  color(color: string) { const i = [K, R, Y, G, B, W].indexOf(color); return this.level && i >= 0 ? this.theme.colors[i] : color; }
  step(title: string, tip: string) { this.steps.push({ title, tip, at: this.steps.length ? this.time : 0 }); this.time += 450; return this; }
  stroke(points: XY[], color: string, width: number, brush: StrokeBrush = 'pen', opacity = 1, quick = false, sampleSpacing = 10) {
    if (!points.length) return this;
    const seed = this.drawing.strokes.length * 2.17 + this.subject.length;
    const sampled: XY[] = [points[0]]; let distance = 0;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1], b = points[i], length = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (length < 0.1) continue;
      const count = Math.max(1, Math.ceil(length / (sampleSpacing + sampleSpacing * .3 * wave(seed + i))));
      for (let j = 1; j <= count; j++) {
        const t = j / count, offset = Math.sin(Math.PI * t) * wave(distance / 53 + seed) * (quick ? 1.7 : 1.25);
        sampled.push([a[0] + (b[0] - a[0]) * t - (b[1] - a[1]) / length * offset, a[1] + (b[1] - a[1]) * t + (b[0] - a[0]) / length * offset]);
        distance += length / count;
      }
    }
    // 大形采用低频的不规则曲率，避免圆规感；幅度小，不能变成刻意抖动的锯齿。
    const path = sampled.map(([x, y], i): XY => {
      const envelope = Math.sin(Math.PI * i / Math.max(1, sampled.length - 1));
      return [x + wave(i / 7 + seed) * envelope * 1.4, y + wave(i / 9 + seed + 4) * envelope * 1.4];
    });
    let t = this.time;
    this.drawing.strokes.push({ tool: 'pen', brush, color: this.color(color), width: Math.min(70, Math.max(1, width * (0.94 + 0.06 * wave(seed)))), ...(opacity < 1 ? { opacity } : {}), points: path.map(([x, y], i) => {
      if (i) t += Math.max(6, Math.round(Math.hypot(x - path[i - 1][0], y - path[i - 1][1]) * (quick ? 0.7 : 2.7) * (1 + 0.16 * wave(i / 4 + seed))));
      return { x: Math.round(Math.max(1, Math.min(999, x)) * 10) / 10, y: Math.round(Math.max(1, Math.min(999, y)) * 10) / 10, t };
    }) });
    this.time = t + Math.round((quick ? 55 : 155) * (1.2 + 0.25 * wave(seed))); return this;
  }
  line(points: XY[], color = K, width = 9, quick = false) {
    return this.stroke(points, color, this.level && color === K ? width * 0.72 : width, this.level && !quick && width < 15 ? 'ink' : 'pen', 1, quick);
  }
  dot(x: number, y: number, size: number, color = K) { return this.stroke([[x, y]], color, size); }
  // 斜向、微弯且不等距的长笔势。端点内收，色块允许少量纸白自然透出。
  fill(points: XY[], color: string) {
    const angle = -0.22 + wave(points[0][0] / 91) * 0.18, cs = Math.cos(angle), sn = Math.sin(angle);
    const polygon = points.map(([x, y]): XY => [x * cs + y * sn, -x * sn + y * cs]);
    const min = Math.min(...polygon.map(p => p[1])), max = Math.max(...polygon.map(p => p[1]));
    let row = 0;
    for (let y = min + 8; y < max - 7; y += 12 + wave(row) * 1.2) {
      const crosses: number[] = [];
      polygon.forEach((a, i) => { const b = polygon[(i + 1) % polygon.length]; if ((a[1] <= y && b[1] > y) || (b[1] <= y && a[1] > y)) crosses.push(a[0] + (y - a[1]) * (b[0] - a[0]) / (b[1] - a[1])); });
      crosses.sort((a, b) => a - b);
      for (let i = 0; i + 1 < crosses.length; i += 2) {
        const a = crosses[i] + 10, b = crosses[i + 1] - 10;
        if (b <= a) continue;
        const path: XY[] = Array.from({ length: Math.max(3, Math.ceil((b - a) / 18)) }, () => [0, 0]);
        for (let j = 0; j < path.length; j++) { const t = j / (path.length - 1), xx = a + (b - a) * t, yy = y + Math.sin(t * Math.PI) * wave(row * 0.8) * 3; path[j] = [xx * cs - yy * sn, xx * sn + yy * cs]; }
        if (row++ % 2) path.reverse(); this.stroke(path, color, 19, 'pen', 1, true);
      }
    }
    return this;
  }
  /**
   * 玻璃、光锥等大面积罩色：相邻排笔属于同一条半透明笔迹，只混合一次，
   * 避免对 fill() 的每一笔设置 opacity 后在排笔交界处产生深浅条带。
   * 仅用于每条水平扫描线最多一个区间的区域；带孔或多岛区域需拆开设计。
   */
  glaze(points: XY[], color: string, opacity = .16, brushWidth = 68) {
    const path: XY[] = [];
    const width = Math.max(10, Math.min(68, brushWidth)), inset = width * .46;
    const min = Math.min(...points.map(p => p[1])), max = Math.max(...points.map(p => p[1]));
    let row = 0;
    for (let y = min + inset; y < max - inset; y += width * .7) {
      const crosses: number[] = [];
      points.forEach((a, i) => {
        const b = points[(i + 1) % points.length];
        if ((a[1] <= y && b[1] > y) || (b[1] <= y && a[1] > y)) crosses.push(a[0] + (y - a[1]) * (b[0] - a[0]) / (b[1] - a[1]));
      });
      crosses.sort((a, b) => a - b);
      if (crosses.length > 2) throw new Error('罩色区域含孔或分叉，请拆成单个连续色面');
      if (crosses.length !== 2 || crosses[1] - crosses[0] < inset * 2) continue;
      const a: XY = [crosses[0] + inset, y], b: XY = [crosses[1] - inset, y];
      path.push(...(row++ % 2 ? [b, a] : [a, b]));
    }
    // 罩色无需普通线稿的密集采样；按路径长度保留点数余量，细边区域也不超单笔 1,500 点。
    const length = path.reduce((sum, point, i) => i ? sum + Math.hypot(point[0] - path[i - 1][0], point[1] - path[i - 1][1]) : 0, 0);
    const spacing = Math.max(30, length / (Math.max(1, 1400 - path.length) * .7));
    if (path.length) this.stroke(path, color, width, 'translucent', opacity, true, spacing);
    return this;
  }
  shape(points: XY[], color: string, outline = K) {
    this.fill(points, color);
    const contour = close(points);
    if (this.level && contour.length > 20) {
      // 柔和的顺形暗部，用半透明笔轻扫右下边缘，不给每个形状套同一圈黑线。
      const cx = points.reduce((s, p) => s + p[0], 0) / points.length, cy = points.reduce((s, p) => s + p[1], 0) / points.length;
      const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
      const span = Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
      if (span > 100 && color !== K) {
        let segment: XY[] = [];
        const flush = () => { if (segment.length > 3) this.stroke(segment, this.theme.shade, Math.min(32, span * 0.09), 'translucent', this.level === 2 ? 0.22 : 0.18); segment = []; };
        for (const [x, y] of contour) { if (x > cx && y > cy - span * 0.2) segment.push([cx + (x - cx) * 0.84, cy + (y - cy) * 0.84]); else flush(); } flush();
      }
    }
    if (this.level === 2 && contour.length > 24) {
      const n = contour.length; this.stroke(contour.slice(0, Math.floor(n * 0.46)), outline === K ? this.theme.colors[0] : outline, 5, 'ink');
      this.stroke(contour.slice(Math.floor(n * 0.49), Math.floor(n * 0.99)), outline === K ? this.theme.colors[0] : outline, 4.2, 'ink');
    } else this.line(contour, outline, this.level ? 6.5 : 7.5);
    return this;
  }
  ring(x: number, y: number, rx: number, ry = rx, color = K, width = 9) { return this.line(ellipse(x, y, rx, ry), color, width); }
  leaf(x: number, y: number, dx: number, dy: number) {
    const path = curve([x, y], [[x + dx * 0.1 - dy * 0.35, y + dy * 0.1 + dx * 0.35], [x + dx * 0.8 - dy * 0.3, y + dy * 0.8 + dx * 0.3], [x + dx, y + dy]], [[x + dx * 0.8 + dy * 0.3, y + dy * 0.8 - dx * 0.3], [x + dx * 0.1 + dy * 0.35, y + dy * 0.1 - dx * 0.35], [x, y]]);
    this.shape(path, G, G); this.line([[x, y], [x + dx * 0.8, y + dy * 0.8]], this.level ? this.theme.light : W, 4);
  }
  star(x: number, y: number, r: number, color = Y) {
    const p: XY[] = Array.from({ length: 10 }, (_, i) => { const a = -Math.PI / 2 + i * Math.PI / 5, radius = i % 2 ? r * 0.42 : r; return [x + Math.cos(a) * radius, y + Math.sin(a) * radius]; });
    this.shape(p, color, color);
  }
  done(id: string, title: string, skill: string, keywords: string[] = []): DrawingLesson {
    const used = new Set(this.drawing.strokes.map(s => s.color)); if (this.drawing.background) used.add(this.drawing.background);
    // 贪心覆盖实际用色，优先覆盖更多颜色，避免把同色的多个包全部列出来。
    const palettes: string[] = [];
    while (used.size) {
      const best = [...PALETTES].sort((a, b) => b.colors.filter(c => used.has(c)).length - a.colors.filter(c => used.has(c)).length)[0];
      if (!best.colors.some(c => used.has(c))) throw new Error('例画使用了目录外的颜色');
      palettes.push(best.id); best.colors.forEach(c => used.delete(c));
    }
    return { id, title, skill, keywords: [...new Set([title, skill, this.theme.style, ...keywords])], level: this.level, style: this.level === 2 ? this.theme.style : LESSON_LEVELS[this.level].description, drawing: this.drawing, steps: this.steps, palettes, brushes: [...new Set(this.drawing.strokes.map(s => s.brush ?? 'pen'))] };
  }
}
